import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Wand20Regular, Dismiss20Regular, ArrowClockwise16Regular } from '@fluentui/react-icons';
import { OverlayDrawer, DrawerBody, Textarea } from '@fluentui/react-components';
import { toast } from 'react-toastify';

import styles from '../IRL/css/index.module.css';
import { addTempMessage, removeMessageById, clearGeneralChat, sendGeneralChatMessage } from '../../redux/generalChatSlice';

const GeneralGPTChatDrawer = ({ show, setShow }) => {
    const dispatch = useDispatch();
    const { messages, loading, error } = useSelector(state => state.generalChat || { messages: [], loading: false, error: null });

    const [input, setInput] = React.useState('');
    const chatContainerRef = React.useRef(null);

    const scrollToBottom = () => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    };

    React.useEffect(() => {
        scrollToBottom();
    }, [messages, loading, show]);

    React.useEffect(() => {
        if (error) toast.error(error);
    }, [error]);

    const formatMessageContent = content => {
        if (!content) return '';

        const escapeHtml = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

        const formatInline = s => {
            const escaped = escapeHtml(s);
            // Bold then italic (order matters).
            return escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>');
        };

        const lines = String(content).replace(/\r\n/g, '\n').split('\n');
        const out = [];

        let paragraphLines = [];
        const flushParagraph = () => {
            if (!paragraphLines.length) return;
            const text = paragraphLines.join('<br/>');
            out.push(`<p>${formatInline(text)}</p>`);
            paragraphLines = [];
        };

        let i = 0;
        while (i < lines.length) {
            const rawLine = lines[i];
            const line = rawLine.trimEnd();

            // Blank line -> paragraph separator
            if (!line.trim()) {
                flushParagraph();
                i += 1;
                continue;
            }

            // Headings
            const h3 = line.match(/^###\s+(.+)$/);
            if (h3) {
                flushParagraph();
                out.push(`<h3>${formatInline(h3[1])}</h3>`);
                i += 1;
                continue;
            }
            const h2 = line.match(/^##\s+(.+)$/);
            if (h2) {
                flushParagraph();
                out.push(`<h2>${formatInline(h2[1])}</h2>`);
                i += 1;
                continue;
            }
            const h1 = line.match(/^#\s+(.+)$/);
            if (h1) {
                flushParagraph();
                out.push(`<h1>${formatInline(h1[1])}</h1>`);
                i += 1;
                continue;
            }

            // Unordered list
            const ulMatch = line.match(/^\s*-\s+(.+)$/);
            if (ulMatch) {
                flushParagraph();
                const items = [];
                while (i < lines.length) {
                    const l = lines[i].trimEnd();
                    const m = l.match(/^\s*-\s+(.+)$/);
                    if (!m) break;
                    items.push(m[1]);
                    i += 1;
                }
                out.push(`<ul>${items.map(item => `<li>${formatInline(item)}</li>`).join('')}</ul>`);
                continue;
            }

            // Ordered list
            const olMatch = line.match(/^\s*\d+\.\s+(.+)$/);
            if (olMatch) {
                flushParagraph();
                const items = [];
                while (i < lines.length) {
                    const l = lines[i].trimEnd();
                    const m = l.match(/^\s*\d+\.\s+(.+)$/);
                    if (!m) break;
                    items.push(m[1]);
                    i += 1;
                }
                out.push(`<ol>${items.map(item => `<li>${formatInline(item)}</li>`).join('')}</ol>`);
                continue;
            }

            // Default: treat as part of a paragraph.
            paragraphLines.push(line);
            i += 1;
        }

        flushParagraph();
        return out.join('');
    };

    const handleSend = async () => {
        const trimmed = (input || '').trim();
        if (!trimmed || loading) return;

        const now = Date.now();
        const tempUserId = `temp-user-${now}`;
        const tempAssistantId = `temp-assistant-${now}`;

        dispatch(
            addTempMessage({
                id: tempUserId,
                role: 'user',
                content: trimmed,
                created_at: new Date().toISOString(),
                is_temp: true,
            }),
        );

        dispatch(
            addTempMessage({
                id: tempAssistantId,
                role: 'assistant',
                content: '...', // placeholder; we render typing dots
                created_at: new Date().toISOString(),
                is_temp: true,
            }),
        );

        setInput('');

        // Build prompt context from current non-temp messages plus the new user message.
        const nonTempMessages = (messages || []).filter(m => !m.is_temp);
        const openaiMessages = [...nonTempMessages.map(m => ({ role: m.role, content: m.content })), { role: 'user', content: trimmed }];

        try {
            await dispatch(sendGeneralChatMessage({ openaiMessages, tempUserId, tempAssistantId })).unwrap();
        } catch (e) {
            // slice reducer removes temp bubbles on failure; show a toast as well.
            toast.error(e || 'Failed to send message');
        }
    };

    const handleKeyDown = e => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleClear = () => {
        dispatch(clearGeneralChat());
    };

    const handleRegenerate = async () => {
        const nonTempMessages = (messages || []).filter(m => !m.is_temp);
        if (!nonTempMessages.length) return toast.error('No messages to regenerate');

        // Find last user message index
        let lastUserIndex = -1;
        for (let i = nonTempMessages.length - 1; i >= 0; i--) {
            if (nonTempMessages[i].role === 'user') {
                lastUserIndex = i;
                break;
            }
        }

        if (lastUserIndex === -1) return toast.error('No user message found to regenerate');

        // Remove last assistant message after that user message (ChatGPT-like behavior)
        const lastAssistant = [...nonTempMessages]
            .slice(lastUserIndex + 1)
            .reverse()
            .find(m => m.role === 'assistant');

        if (lastAssistant) {
            dispatch(removeMessageById({ id: lastAssistant.id }));
        }

        const now = Date.now();
        const tempAssistantId = `temp-assistant-${now}`;

        dispatch(
            addTempMessage({
                id: tempAssistantId,
                role: 'assistant',
                content: '...', // placeholder
                created_at: new Date().toISOString(),
                is_temp: true,
            }),
        );

        const openaiMessages = nonTempMessages.slice(0, lastUserIndex + 1).map(m => ({ role: m.role, content: m.content }));

        try {
            await dispatch(sendGeneralChatMessage({ openaiMessages, tempAssistantId })).unwrap();
        } catch (e) {
            toast.error(e || 'Failed to regenerate response');
        }
    };

    const lastAssistantId = (messages || []).filter(m => !m.is_temp && m.role === 'assistant').slice(-1)[0]?.id || null;

    return (
        <OverlayDrawer
            style={{ minWidth: 872 }}
            type="overlay"
            position="end"
            open={show}
            onOpenChange={(_, { open }) => {
                setShow(open);
            }}>
            <DrawerBody className={`position-relative ${styles.drawerContent}`} style={{ padding: 0, display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <div className="d-flex align-items-center justify-content-between px-3 mt-3 mb-3">
                    <div className="d-flex align-items-center">
                        <Wand20Regular color="var(--Brand-Secondary)" className="me-1" />
                        <span className={styles.aiTitle}>Ask Lens AI</span>
                    </div>
                    <div className="d-flex align-items-center">
                        <button
                            onClick={handleClear}
                            className="btn btn-outline-secondary btn-sm me-2"
                            disabled={loading || !messages || messages.length === 0}
                            title="Clear chat history">
                            Clear
                        </button>
                        <Dismiss20Regular onClick={() => setShow(false)} role="button" color="var(--Neutral-Gray-6)" />
                    </div>
                </div>

                <div ref={chatContainerRef} className="flex-grow-1 px-3" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                    {!messages || messages.length === 0 ? (
                        <div className="text-center text-muted mt-5">
                            <Wand20Regular className="mb-3" style={{ fontSize: '3rem', opacity: 0.3 }} />
                            <h5 className="mb-3 fw-bold">General chat</h5>
                            <p className="mb-3">Ask anything you would normally ask ChatGPT.</p>
                            <p className="mb-3">Try:</p>
                            <div className="text-start" style={{ maxWidth: '356px', margin: '0 auto' }}>
                                <div className="p-3 mb-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)' }}>
                                    "Explain OAuth vs SAML like I'm a PM."
                                </div>
                                <div className="p-3 mb-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)' }}>
                                    "Draft an email requesting a vendor security roadmap."
                                </div>
                                <div className="p-3 mb-2 rounded" style={{ backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)' }}>
                                    "Give me a short checklist for API rate limiting."
                                </div>
                            </div>
                        </div>
                    ) : (
                        (messages || []).map(message => {
                            if (!message) return null;
                            const isUser = message.role === 'user';
                            const isAssistant = message.role === 'assistant';
                            const isTempAssistant = message.is_temp && isAssistant;
                            const isLastAIResponse = isAssistant && !message.is_temp && message.id === lastAssistantId;

                            return (
                                <div key={message.id} className="mb-4 position-relative" style={{ textAlign: isUser ? 'right' : 'left' }}>
                                    <div
                                        className={`d-inline-block p-3 rounded ${isUser ? styles.userMessage : styles.assistantMessage}`}
                                        style={{
                                            maxWidth: '80%',
                                            textAlign: 'left',
                                            wordWrap: 'break-word',
                                        }}>
                                        {isTempAssistant && loading ? (
                                            <div className={styles['typing-dots']}>
                                                <span></span>
                                                <span></span>
                                                <span></span>
                                            </div>
                                        ) : (
                                            <div dangerouslySetInnerHTML={{ __html: formatMessageContent(message.content || '') }} />
                                        )}
                                    </div>

                                    {isLastAIResponse && (
                                        <ArrowClockwise16Regular className="position-absolute end-0 mt-3" role="button" onClick={handleRegenerate} color="var(--Brand-Secondary)" />
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                <div className="p-3" style={{ backgroundColor: 'var(--Neutral-Gray-1)' }}>
                    <div className="d-flex flex-column">
                        <div className="mb-3">
                            <Textarea
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Ask the AI anything ..."
                                style={{
                                    resize: 'vertical',
                                    width: '100%',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    padding: '12px',
                                    fontSize: '14px',
                                    minHeight: '90px',
                                }}
                                disabled={loading}
                            />
                        </div>
                        <div className="d-flex justify-content-start">
                            <button onClick={handleSend} className={styles.askAISendButton} disabled={!input.trim() || loading}>
                                {loading ? 'Sending...' : 'Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default GeneralGPTChatDrawer;
